import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { CatanPlayer } from '../models/catan.model';

const STORAGE_KEY = 'catan_pin';

interface StoredPin {
  pin: string;
  playerId: string;
  displayName: string;
}

/**
 * Osobni PIN igrača — istovremeno identitet i ovlast za unos.
 *
 * Otključavanjem klijent sazna tko je, pa uz PIN šalje i id igrača: backend tada
 * provjerava jedan hash umjesto da skenira sve igrače.
 */
@Injectable({ providedIn: 'root' })
export class CatanPinService {
  private http = inject(HttpClient);

  private stored = signal<StoredPin | null>(this.read());

  readonly pin = computed(() => this.stored()?.pin ?? null);
  readonly playerId = computed(() => this.stored()?.playerId ?? null);
  readonly displayName = computed(() => this.stored()?.displayName ?? null);
  readonly unlocked = computed(() => this.stored() !== null);

  verify(pin: string) {
    return this.http.post<{ valid: boolean; player: CatanPlayer }>('/api/catan/pin/verify', { pin }).pipe(
      tap(res => {
        if (res.valid) {
          this.store({ pin, playerId: res.player.id, displayName: res.player.displayName });
        }
      }),
    );
  }

  /** Nakon promjene PIN-a treba osvježiti spremljenu vrijednost, inače idući zahtjev pada na 403. */
  replacePin(pin: string): void {
    const current = this.stored();
    if (current) {
      this.store({ ...current, pin });
    }
  }

  isMe(playerId: string): boolean {
    return this.playerId() === playerId;
  }

  lock(): void {
    this.stored.set(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // privatni prozor / blokiran storage — signal je i dalje ispravan za ovu sesiju
    }
  }

  private store(value: StoredPin): void {
    this.stored.set(value);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      // vidi gore
    }
  }

  private read(): StoredPin | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<StoredPin>;
      if (!parsed.pin || !parsed.playerId) return null;

      return { pin: parsed.pin, playerId: parsed.playerId, displayName: parsed.displayName ?? '' };
    } catch {
      return null;
    }
  }
}
